import { Grid, Paper, Button, Box, CircularProgress, Typography, List, ListItem, ListItemText } from '@mui/material';
import { Email as EmailIcon, Mail, Phone, Edit, ArrowForward } from '@mui/icons-material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getDonorById, getDonorEmailLog } from 'src/shared/services/donorService';
import { getAllInvoices } from 'src/shared/services/invoiceService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import MemberAvatar from 'src/modules/member/components/memberAvatar';
import { MemberDetailBirthDate } from 'src/modules/member/components/memberDetailBirthDate';
import { MemberDetailLink } from 'src/modules/member/components/memberDetailLink';
import { formatPersonalNumber } from 'src/shared/functions/formatStringToDate';
import { formatDate } from 'src/shared/functions/formatDate';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Invoice } from 'src/modules/invoice/models/invoice';
import { MemberEmail } from 'src/modules/member/models/memberEmail';
import { InvoiceSearchFilter } from 'src/modules/invoice/models/invoiceSearchFilter';
import { InvoiceStatuses } from 'src/modules/invoice/components/invoiceStatuses';
import { DonorResponse } from '../models/donor';
import { DonorDetailSubscriptions } from '../components/donorDetailSubscriptions';
import { DonorEditModal } from '../components/donorEditModal';
import { DonorAutogiroSection } from '../components/donorAutogiroSection';
import { DonorEmailLogsModal } from '../components/donorEmailLogsModal';

type DonorsDetailParams = {
  id: string;
};

export const DonorsDetailPage = () => {
  const params = useParams<DonorsDetailParams>();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { dateStamp, setDateStamp } = useGlobals();
  const [donor, setDonor] = useState<DonorResponse>();
  const [loading, setLoading] = useState(true);
  const [editDonor, setEditDonor] = useState(false);
  const [openEmailLogs, setOpenEmailLogs] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [emailLog, setEmailLog] = useState<MemberEmail[]>([]);

  const { t } = useTranslation(['gift', 'member', 'common', 'invoice']);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDonorById(params.id ?? '');
      if (isMounted()) {
        ConsoleHelper.log('DonorsDetailPage', result.data, dateStamp);
        setDonor(result.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [params.id, isMounted, dateStamp]);

  // Hämta fakturor (gåvor) för donorn via donor_id
  useEffect(() => {
    if (!donor?.id) return;
    const filter: InvoiceSearchFilter = {
      filter: '',
      searchString: '',
      donor_id: donor.id,
      pageIndex: 0,
      pageSize: 50,
    };
    getAllInvoices(0, 50, 'created', 'desc', filter)
      .then((res) => {
        if (isMounted()) setInvoices(res.data.results ?? []);
      })
      .catch((e) => ConsoleHelper.log(e));
  }, [donor?.id, isMounted]);

  // Hämta e-postlogg via Donor/{id}/Email
  useEffect(() => {
    if (!donor?.id) return;
    getDonorEmailLog(donor.id, 5)
      .then((res) => {
        if (isMounted()) setEmailLog(res.data.results ?? []);
      })
      .catch((e) => ConsoleHelper.log(e));
  }, [donor?.id, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [firstName, lastName] = donor?.name ? donor.name.split(' ') : ['', ''];
  const currentYear = new Date().getFullYear().toString();
  const paidInvoices = invoices.filter((inv) => inv.paid > 0);
  const totalGifts = paidInvoices.length;
  const giftsThisYear = paidInvoices.filter((inv) => (inv.paidDate || inv.created || '').startsWith(currentYear)).length;
  const totalAmount = paidInvoices.reduce((sum, inv) => sum + inv.paid, 0);

  const navigateToGifts = () => {
    localStorage.setItem(
      LocalStorageKeys.GiftSearchFilter,
      JSON.stringify({
        donor_id: donor?.id,
        donorDisplayName: donor?.name,
        isHandled: null,
      }),
    );
    navigate(RoutesEnum.Gifts);
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} lg={11}>
          <Grid container alignItems='center' justifyContent='space-between'>
            <Grid item xs='auto'>
              <MainContentHeader title={t('gift:donors')} isLoading={loading} />
            </Grid>
            {!loading && donor && (
              <Grid item xs='auto'>
                <Button size='small' variant='outlined' onClick={() => setEditDonor(true)} endIcon={<Edit />} disabled={donor.id === null || loading}>
                  {t('common:button.edit')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} lg={11}>
          <Paper sx={{ p: 3 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && donor && (
              <GridWrapper>
                {/* Vänsterkolumn */}
                <Grid item xs={12} xl={8} pr={{ xl: '24px' }}>
                  <GridWrapper>
                    <Grid item xs={12}>
                      <ContentSection>
                        <GridWrapper>
                          <Grid item xs='auto'>
                            <MemberAvatar firstName={firstName ?? ''} lastName={lastName ?? ''} />
                          </Grid>
                          <Grid item xs='auto'>
                            <Typography variant='h6' sx={{ fontWeight: 700 }} gutterBottom>
                              {donor.name}
                            </Typography>
                            <MemberDetailBirthDate birthDate={formatPersonalNumber(donor.personalIdentityNumber) ?? ''} />
                            <MemberDetailLink icon={<EmailIcon sx={{ mr: 1 }} />} linkPrefix='mailto:' details={[donor.email || '']} />
                            <MemberDetailLink icon={<Phone sx={{ mr: 1 }} />} linkPrefix='tel:' details={[donor.phone || '']} />
                          </Grid>
                          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            {[
                              { label: 'Antal gåvor totalt', value: `${totalGifts} st` },
                              { label: 'Antal gåvor i år', value: `${giftsThisYear} st` },
                              { label: 'Totalt belopp', value: `${totalAmount.toLocaleString('sv-SE')} kr` },
                            ].map((kpi) => (
                              <Box
                                key={kpi.label}
                                onClick={navigateToGifts}
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.15s',
                                  '&:hover': { backgroundColor: 'action.hover' },
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.2 }}>
                                    {kpi.label}
                                  </Typography>
                                  <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {kpi.value}
                                  </Typography>
                                </Box>
                                <ArrowForward sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                              </Box>
                            ))}
                          </Grid>
                        </GridWrapper>
                      </ContentSection>
                    </Grid>

                    <Grid item xs={12}>
                      <DonorDetailSubscriptions donorId={donor.id} subscriptions={donor.subscriptions ?? []} />
                    </Grid>

                    <Grid item xs={12}>
                      <ContentSection
                        header={t('member:emailLog.title')}
                        button={
                          emailLog && emailLog.length > 0 ? (
                            <Button
                              type='button'
                              variant='outlined'
                              size='small'
                              endIcon={<Mail fontSize='small' />}
                              onClick={() => setOpenEmailLogs(true)}
                            >
                              {t('member:emailLog.title')}
                            </Button>
                          ) : undefined
                        }
                      >
                        <List sx={{ mt: -2.0 }} dense>
                          {emailLog.length > 0 ? (
                            emailLog.map((log) => (
                              <ListItem key={log.mail_id} divider sx={{ py: 0.5, px: 1 }}>
                                <ListItemText
                                  disableTypography
                                  primary={
                                    <Box display='flex' flexDirection='row' alignItems='flex-start'>
                                      <Box flex={1} pr={1}>
                                        <Typography variant='body1' sx={{ wordBreak: 'break-word' }}>
                                          {log.subject}
                                        </Typography>
                                      </Box>
                                      <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {DateHelper.getDateTime(log.circularExecutingTimeStamp)}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText primary={t('member:emailLog.noEmails')} primaryTypographyProps={{ color: 'text.secondary' }} />
                            </ListItem>
                          )}
                        </List>
                      </ContentSection>
                    </Grid>
                  </GridWrapper>
                </Grid>

                {/* Högerkolumn */}
                <Grid
                  item
                  xs={12}
                  xl={4}
                  sx={(theme) => ({
                    borderLeft: { xs: 'none', xl: `1px solid ${theme.palette.divider}` },
                    pl: { xl: 3 },
                  })}
                >
                  <GridWrapper>
                    <Grid item xs={12}>
                      <ContentSection header='Betalmetod'>
                        <DonorAutogiroSection donor={donor} refetchCallback={fetchData} />
                      </ContentSection>
                    </Grid>
                    <Grid item xs={12}>
                      <ContentSection header='Senaste fakturor'>
                        {invoices.length > 0 ? (
                          invoices.slice(0, 10).map((inv) => (
                            <Box
                              key={inv.invoice_id}
                              sx={{
                                display: 'flex',
                                gap: 1.5,
                                py: 1.25,
                                borderBottom: (thm) => `1px solid ${thm.palette.divider}`,
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box display='flex' alignItems='flex-start' justifyContent='space-between'>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                      {inv.amount?.toLocaleString('sv-SE')} kr —{' '}
                                      <Typography
                                        component='span'
                                        variant='body2'
                                        onClick={() => navigate(`${RoutesEnum.Invoices}/${inv.invoice_id}`)}
                                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, fontWeight: 400 }}
                                      >
                                        #{inv.ocr || inv.invoice_id}
                                      </Typography>
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                      {formatDate(inv.created)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                    <InvoiceStatuses status={inv.status} chipSize='small' />
                                    <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                                      {inv.paidDate ? formatDate(inv.paidDate) : '—'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
                            Inga gåvor ännu
                          </Typography>
                        )}
                      </ContentSection>
                    </Grid>
                    {donor.createdAt && (
                      <Grid item xs={12}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 16 }} variant='h6'>
                            Skapad:
                          </Typography>
                          <Typography variant='h6' sx={{ fontWeight: 400, fontSize: 16 }}>
                            {DateHelper.getDateTime(donor.createdAt)}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </GridWrapper>
                </Grid>
              </GridWrapper>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {donor && editDonor && (
        <DonorEditModal
          donor={donor}
          onClose={() => {
            setEditDonor(false);
          }}
          onSave={() => {
            setEditDonor(false);
            setDateStamp(new Date());
          }}
          modalOpen={editDonor}
        />
      )}
      {donor && openEmailLogs && (
        <DonorEmailLogsModal
          modalOpen={openEmailLogs}
          onClose={() => setOpenEmailLogs(false)}
          donorId={donor.id}
          donorName={donor.name || ''}
          donorEmail={donor.email}
        />
      )}
    </>
  );
};
